import type {
  IAnnouncementClientFolder,
  IDatabaseClientFolder,
  IDocumentClientFolder,
  ILinkClientFolder,
  IRawResourceAnnouncementFolder,
  IRawResourceDatabaseFolder,
  IRawResourceDocumentFolder,
  IRawResourceItemType,
  IRawResourceLinkFolder,
  IRawResourceTreeAnnouncementNodes,
  IRawResourceTreeDatabaseNodes,
  IRawResourceTreeDocumentNodes,
  IRawResourceTreeLinkNodes,
  IResourceAnnouncementFolder,
  IResourceDatabaseFolder,
  IResourceDocumentFolder,
  IResourceItem,
  IResourceItemTransformed,
  IResourceLinkFolder,
} from '../types';

import { ensureTimezoneAwareness } from '@/helpers';

/**
 * Resource item transformations
 */

export const makeResourceItemTimezoneAware = <T extends IResourceItem>(item: T): T => ({
  ...item,
  dateStart: ensureTimezoneAwareness(item.dateStart),
  dateEnd: item.dateEnd && ensureTimezoneAwareness(item.dateEnd),
  dateCreated: ensureTimezoneAwareness(item.dateCreated),
  dateModified: item.dateModified && ensureTimezoneAwareness(item.dateModified),
});

export const transformResourceItemFieldsToCamel = <T extends IRawResourceItemType>(
  item: T,
): IResourceItemTransformed<T> => {
  const { createdBy_Name, modifiedBy_Name, ...rest } = item;

  return {
    ...rest,
    createdByName: createdBy_Name,
    modifiedByName: modifiedBy_Name,
  } as unknown as IResourceItemTransformed<T>;
};

export const transformRawResourceItem = <T extends IRawResourceItemType>(item: T): IResourceItemTransformed<T> => {
  const camelCased = transformResourceItemFieldsToCamel(item);
  const transformedItem = makeResourceItemTimezoneAware(camelCased);

  return transformedItem;
};

/**
 * Resource folder transformations
 */

export const transformLinkFolder = (rawFolder: IRawResourceLinkFolder): IResourceLinkFolder => ({
  ...rawFolder,
  links: rawFolder.links?.map(transformRawResourceItem) ?? [],
  folders: rawFolder.folders?.map(transformLinkFolder) ?? [],
});

export const transformAnnouncementFolder = (
  rawFolder: IRawResourceAnnouncementFolder,
): IResourceAnnouncementFolder => ({
  ...rawFolder,
  announcements: rawFolder.announcements?.map(transformRawResourceItem) ?? [],
  folders: rawFolder.folders?.map(transformAnnouncementFolder) ?? [],
});

export const transformDocumentFolder = (rawFolder: IRawResourceDocumentFolder): IResourceDocumentFolder => ({
  ...rawFolder,
  docs: rawFolder.docs?.map(transformRawResourceItem) ?? [],
  folders: rawFolder.folders?.map(transformDocumentFolder) ?? [],
});

export const transformDatabaseFolder = (rawFolder: IRawResourceDatabaseFolder): IResourceDatabaseFolder => ({
  ...rawFolder,
  folders: null,
  folderId: rawFolder.id,
  fields: [],
});

/**
 * Resource tree nodes transformations
 */

export const transformResourceTreeLinkNodes = (nodes: IRawResourceTreeLinkNodes): ILinkClientFolder[] => {
  return nodes.nodes.map(node => ({
    ...node,
    folders: node.folders?.map(transformLinkFolder) ?? [],
  }));
};

export const transformResourceTreeAnnouncementNodes = (
  nodes: IRawResourceTreeAnnouncementNodes,
): IAnnouncementClientFolder[] => {
  return nodes.nodes.map(node => ({
    ...node,
    folders: node.folders?.map(transformAnnouncementFolder) ?? [],
  }));
};

export const transformResourceTreeDocumentNodes = (nodes: IRawResourceTreeDocumentNodes): IDocumentClientFolder[] => {
  return nodes.nodes.map(node => ({
    ...node,
    folders: node.folders?.map(transformDocumentFolder) ?? [],
  }));
};

export const transformResourceTreeDatabaseNodes = (nodes: IRawResourceTreeDatabaseNodes): IDatabaseClientFolder[] => {
  return nodes.nodes.map(node => ({
    ...node,
    databases: node.databases?.map(transformDatabaseFolder) ?? [],
  }));
};
